import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import H2 from '../../../../components/Titles/h2';
import PaiementCard from '../../../../components/Inputs/paiement-card';

import lodash from "lodash";

export default function PaiementMethodFormQuotation({ quotation, group, quotations = [], onCardClicked }) {
    const { t } = useTranslation();
    const [cardClicked, setCardClicked] = useState(null);

    // Normalize to a list we can reason about
    const items = useMemo(() => {
        if (Array.isArray(quotations) && quotations.length) return quotations;
        return quotation ? [quotation] : [];
    }, [quotation, quotations]);

    const payModeLockedTotal = lodash.get(group, 'metadata.payMode') === 'total';

    // Deposit eligibility per item
    const isEligibleForDeposit = (q) => {
        const enableDeposit = lodash.get(q, 'prices.enableDeposit', false);
        const depositPercentage = Number(lodash.get(q, 'prices.depositPercentage', 0));
        const startDate = lodash.get(q, 'dates.startDate');

        if (!enableDeposit) return false;
        if (depositPercentage <= 0) return false;
        if (depositPercentage >= 100) return false;
        if (!startDate) return false;

        const today = new Date();
        const start = new Date(startDate);

        today.setHours(0, 0, 0, 0);
        start.setHours(0, 0, 0, 0);

        const diffInDays = Math.ceil((start - today) / (1000 * 60 * 60 * 24));

        return diffInDays >= 30;
    };

    const anyDepositEligible = useMemo(
        () => items.some(isEligibleForDeposit),
        [items]
    );

    const allDepositEligible = useMemo(
        () => items.length > 0 && items.every(isEligibleForDeposit),
        [items]
    );

    // Final toggle for showing "pay later"
    const canDeposit = !payModeLockedTotal && anyDepositEligible;

    // Mixed note (some eligible, some not)
    const mixedEligibility = canDeposit && !allDepositEligible;

    // Default selection: if deposit not allowed, force "payNow"
    useEffect(() => {
        if (!canDeposit && cardClicked !== 'payNow') {
            setCardClicked('payNow');
            onCardClicked?.('payNow');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canDeposit]);

    const handleChildClick = (name) => {
        setCardClicked(name);
        onCardClicked?.(name);
    };

    return (
        <div className='px-4 md:px-0'>
            <H2 title={t('pageQuotation.step3.choosePaiementMethod')} />

            <div className="grid gap-y-8 grid-cols-1 mt-10 md:grid-cols-2 md:gap-x-32">
                <PaiementCard
                    name="payNow"
                    title={t('pageQuotation.step3.payNow.title')}
                    description={t('pageQuotation.step3.payNow.shortDescription')}
                    iconKey="faCreditCard"
                    onClick={handleChildClick}
                    selected={cardClicked}
                />

                {canDeposit && (
                    <PaiementCard
                        name="payLater"
                        title={t('pageQuotation.step3.payLater.title')}
                        description={t('pageQuotation.step3.payLater.shortDescription')}
                        iconKey="faClock"
                        onClick={handleChildClick}
                        selected={cardClicked}
                    />
                )}

                {/* If you want to keep these later:
        <PaiementCard
          name="payByTransfer"
          title={t('pageQuotation.step3.payByTransfer.title')}
          description={t('pageQuotation.step3.payByTransfer.shortDescription')}
          iconKey="faMoneyBillTransfer"
          onClick={handleChildClick}
          selected={cardClicked}
        />
        <PaiementCard
          name="payByPhone"
          title={t('pageQuotation.step3.payByPhone.title')}
          description={t('pageQuotation.step3.payByPhone.shortDescription')}
          iconKey="faPhone"
          onClick={handleChildClick}
          selected={cardClicked}
        />
        */}
            </div>

            {/* Subtle note when only some quotations accept deposits */}
            {mixedEligibility && (
                <div className="mt-4 text-sm text-description">
                    {t('pageQuotation.step3.payLater.mixedNote') ||
                        'Remarque : l’acompte ne s’applique qu’aux devis éligibles ; les autres seront réglés en totalité.'}
                </div>
            )}

            {/* Optional: if pay mode is locked to total at group level */}
            {payModeLockedTotal && (
                <div className="mt-2 text-sm text-description">
                    {t('pageQuotation.step3.payModeLockedTotal') ||
                        'Le paiement en une fois est imposé pour ce groupe.'}
                </div>
            )}
        </div>
    );
};